import { Controller } from "@hotwired/stimulus"

const MIN = 1
const MAX = 6

// Zoom and pan for a floor plan. The stage scales inside the viewport; pins
// read --pin-scale to counter-scale, so they stay the same size on screen and
// crowded spots spread apart as you zoom in.
//   + / − buttons, Ctrl/⌘ + scroll, pinch, double-click to zoom; drag to pan.
export default class extends Controller {
  static targets = ["viewport", "stage", "reset"]

  declare readonly viewportTarget: HTMLElement
  declare readonly stageTarget: HTMLElement
  declare readonly resetTarget: HTMLElement

  private scale = 1
  private x = 0
  private y = 0
  private pointers = new Map<number, { x: number; y: number }>()
  private panStart: { x: number; y: number; px: number; py: number } | null = null
  private panned = false
  private pinchStart: { distance: number; scale: number } | null = null

  connect() {
    // A drag that pans shouldn't also count as a click on a room or pin.
    this.swallowClick = this.swallowClick.bind(this)
    this.viewportTarget.addEventListener("click", this.swallowClick, true)
  }

  disconnect() {
    this.viewportTarget.removeEventListener("click", this.swallowClick, true)
  }

  zoomIn() { this.zoomAround(this.center(), this.scale * 1.6) }
  zoomOut() { this.zoomAround(this.center(), this.scale / 1.6) }
  reset() { this.set(1, 0, 0) }

  wheel(event: WheelEvent) {
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    this.zoomAround(this.local(event), this.scale * Math.exp(-event.deltaY * 0.0025))
  }

  doubleClick(event: MouseEvent) {
    if ((event.target as HTMLElement).closest("a, button")) return
    this.zoomAround(this.local(event), this.scale * 2)
  }

  pointerDown(event: PointerEvent) {
    if ((event.target as HTMLElement).closest("button")) return
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })
    this.panned = false

    if (this.pointers.size === 2) {
      this.pinchStart = { distance: this.pinchDistance(), scale: this.scale }
      this.panStart = null
    } else if (this.scale > 1) {
      this.panStart = { x: event.clientX, y: event.clientY, px: this.x, py: this.y }
    }
  }

  pointerMove(event: PointerEvent) {
    if (!this.pointers.has(event.pointerId)) return
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (this.pinchStart && this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()]
      const mid = this.local({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 })
      this.zoomAround(mid, this.pinchStart.scale * (this.pinchDistance() / this.pinchStart.distance))
      this.panned = true
      return
    }

    if (!this.panStart) return
    const dx = event.clientX - this.panStart.x
    const dy = event.clientY - this.panStart.y
    if (!this.panned && Math.hypot(dx, dy) < 5) return

    if (!this.panned) this.viewportTarget.setPointerCapture(event.pointerId)
    this.panned = true
    this.viewportTarget.classList.add("is-panning")
    this.set(this.scale, this.panStart.px + dx, this.panStart.py + dy)
  }

  pointerUp(event: PointerEvent) {
    this.pointers.delete(event.pointerId)
    if (this.pointers.size < 2) this.pinchStart = null
    if (this.pointers.size === 0) {
      this.panStart = null
      this.viewportTarget.classList.remove("is-panning")
    }
  }

  private swallowClick(event: Event) {
    if (!this.panned) return
    event.stopPropagation()
    event.preventDefault()
    this.panned = false
  }

  // Zoom so the point under `at` (viewport coordinates) stays put.
  private zoomAround(at: { x: number; y: number }, target: number) {
    const scale = Math.min(MAX, Math.max(MIN, target))
    const stageX = (at.x - this.x) / this.scale
    const stageY = (at.y - this.y) / this.scale
    this.set(scale, at.x - stageX * scale, at.y - stageY * scale)
  }

  // Applies scale and offset, keeping the plan covering the viewport.
  private set(scale: number, x: number, y: number) {
    const { width, height } = this.viewportTarget.getBoundingClientRect()
    this.scale = scale
    this.x = Math.min(0, Math.max(width - width * scale, x))
    this.y = Math.min(0, Math.max(height - height * scale, y))

    this.stageTarget.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`
    this.stageTarget.style.setProperty("--pin-scale", String(1 / this.scale))
    this.viewportTarget.classList.toggle("is-zoomed", this.scale > 1)
    this.resetTarget.hidden = this.scale === 1
  }

  private center() {
    const { width, height } = this.viewportTarget.getBoundingClientRect()
    return { x: width / 2, y: height / 2 }
  }

  private local(event: { clientX: number; clientY: number }) {
    const rect = this.viewportTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  private pinchDistance() {
    const [a, b] = [...this.pointers.values()]
    return Math.hypot(a.x - b.x, a.y - b.y)
  }
}
